import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Product } from 'src/app/models/product.model';
import { Advertiser } from 'src/app/models/advertiser';
import { Meta, Title } from '@angular/platform-browser';
import { categoryEnum, pathRoutingEnum } from 'src/assets/constantEnum';
import { ProductService } from 'src/app/services/product.service';
import { EditProductAction, ProductActionsTypesNgrx } from 'src/app/ngrx/products.action';
import { ProductsState } from 'src/app/ngrx/products.reducer';
import { Store } from '@ngrx/store';
import { Subscription, filter, take } from 'rxjs';
import { Location } from '@angular/common';
import { getItemById, productsFeature } from 'src/app/ngrx/products.selector';
import { CanonicalService } from 'src/app/services/canonical.service';
import { CommentService } from 'src/app/services/comment.service';
import { AuthService } from 'src/app/services/auth.service';
import { AccountService } from 'src/app/services/account.service';
import { ChatService } from 'src/app/services/chat.service';
import { VoteService } from 'src/app/services/vote.service';
import { FavoriteService } from 'src/app/services/favorite.service';
import { AdvertiserService } from 'src/app/services/advertiser.service';
import { ChatRelation } from 'src/app/models/chat';
import { Comment } from 'src/app/models/comment.model';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogContentLogin } from '../../header-sticky/login-dialog/dialog-content-login';
import { I18nService } from 'src/app/services/i18n.service';
import { getVoteLevel, VoteLevel } from 'src/app/shared/vote-level';
import { discountColor } from 'src/app/shared/discount-color';

// Nombre de deals affichés dans « Autres offres du marchand » et « Dans la même catégorie »
const RELATED_COUNT = 4;

@Component({
    selector: 'app-product-details-ngrx',
    templateUrl: './product-details-ngrx.component.html',
    styleUrls: ['./product-details-ngrx.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProductDetailsNgrxComponent implements OnInit, OnDestroy {
  productId!: string;
  product: Product | null = null;
  notFound = false;
  advertiser: Advertiser | null = null;
  codeCopied = false;

  // Même logique que les cartes de la liste (DashboardComponent) : vote unique, score optimiste
  myVote: number | null = null;
  voteScore = 0;
  saved = false;

  advertiserDeals: Product[] = [];
  categoryDeals: Product[] = [];

  comments: Comment[] = [];
  newCommentText: string = '';
  postingComment: boolean = false;
  editingCommentId: string | null = null;
  editCommentText: string = '';
  savingComment: boolean = false;

  private dealSub?: Subscription;
  private subscriptions = new Subscription();

  constructor(private store: Store<any>, private activatedRoute: ActivatedRoute, private router: Router, private productService: ProductService, private title: Title,
    private metaTagService: Meta,
    private canonicalService: CanonicalService,
    private commentService: CommentService,
    private authService: AuthService,
    private accountService: AccountService,
    private chatService: ChatService,
    private voteService: VoteService,
    private favoriteService: FavoriteService,
    private advertiserService: AdvertiserService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private i18n: I18nService,
    private _location: Location) {
  }

  ngOnInit(): void {
    // La page est réutilisée quand on ouvre un autre deal depuis « Autres offres » : on recharge à chaque :id
    this.subscriptions.add(this.activatedRoute.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.loadDeal(id);
      }
    }));

    // Vote et sauvegarde du membre : rechargés après une connexion faite depuis cette page
    this.subscriptions.add(this.authService.loggedIn$.subscribe(() => this.loadMyState()));

    // Listes des membres suivis / ignorés, pour les boutons des commentaires
    if (this.authService.currentUserValue && !this.accountService.accountValue) {
      this.accountService.load().subscribe({ error: () => { } });
    }
  }

  ngOnDestroy(): void {
    this.dealSub?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  private loadDeal(id: string) {
    this.productId = id;
    this.product = null;
    this.notFound = false;
    this.advertiser = null;
    this.codeCopied = false;
    this.myVote = null;
    this.saved = false;
    this.advertiserDeals = [];
    this.categoryDeals = [];
    this.comments = [];
    this.cancelEditComment();
    this.canonicalService.setCanonicalURL();

    // Déjà dans la liste chargée (arrivée depuis une carte) : pas d'appel ; sinon GET /products/:id
    this.dealSub?.unsubscribe();
    this.dealSub = this.store.select(getItemById(id)).pipe(take(1)).subscribe(item => {
      if (item) {
        this.setProduct(item);
        return;
      }
      this.store.dispatch(new EditProductAction(id));
      this.dealSub = this.store.select(productsFeature).pipe(
        filter((state: ProductsState) => state.currentAction?.type === ProductActionsTypesNgrx.EDIT_PRODUCTS_SUCCESS
          || state.currentAction?.type === ProductActionsTypesNgrx.EDIT_PRODUCTS_ERROR),
        take(1),
      ).subscribe(state => {
        const product = state.currentProduct;
        if (product && product._id === id) {
          this.setProduct(product);
        } else {
          this.notFound = true;
        }
      });
    });

    this.loadComments();
  }

  private setProduct(product: Product) {
    this.product = product;
    this.voteScore = product.voteScore ?? 0;
    this.title.setTitle(product.name + " - promodeep.com");
    this.metaTagService.updateTag(
      { name: "description", content: "offre promotionnelle de " + product.percentageDiscount + "% pour " + product.name },
    );
    this.loadMyState();
    this.loadAdvertiser();
    this.loadRelatedDeals();
  }

  private loadMyState() {
    if (!this.product || !this.authService.currentUserValue) {
      return;
    }
    const id = this.product._id;
    this.voteService.getMyVote(id).subscribe({ next: result => this.myVote = result.myVote, error: () => { } });
    this.favoriteService.getMyFavoriteIds([id]).subscribe({ next: favorites => this.saved = !!favorites[id], error: () => { } });
  }

  private loadAdvertiser() {
    const name = this.advertiserName;
    if (!name) {
      return;
    }
    this.advertiserService.getAdvertiserByName(encodeURIComponent(name)).subscribe({
      next: advertiser => this.advertiser = advertiser || null,
      error: () => { },
    });
  }

  private loadRelatedDeals() {
    const product = this.product!;
    // La base contient des deals en double (même titre) : un seul de chaque, jamais celui affiché
    const others = (deals: Product[]) => {
      const names = new Set([product.name]);
      return (deals || []).filter(p => {
        if (p._id === product._id || p.available === false || names.has(p.name)) {
          return false;
        }
        names.add(p.name);
        return true;
      }).slice(0, RELATED_COUNT);
    };
    const page = { limit: RELATED_COUNT * 3, skip: 0 };
    if (this.advertiserName) {
      this.productService.getAdvertiserProductsFilter(encodeURIComponent(this.advertiserName), page).subscribe({
        next: deals => this.advertiserDeals = others(deals),
        error: () => { },
      });
    }
    if (product.category) {
      this.productService.categoryProductsFilter(encodeURIComponent(product.category), page).subscribe({
        // Les offres du même marchand sont déjà listées juste au-dessus
        next: deals => this.categoryDeals = others((deals || []).filter(p => p.advertiserName !== this.advertiserName)),
        error: () => { },
      });
    }
  }

  get advertiserName(): string {
    return String(this.product?.advertiserName || this.product?.advertiser || '');
  }

  get advertiserInitials(): string {
    return this.advertiserName.slice(0, 2).toUpperCase();
  }

  // Clé de traduction de la catégorie (categories.<clé de categoryEnum>), comme le dashboard
  get categoryKey(): string {
    const name = Object.keys(categoryEnum).find(key => (categoryEnum as any)[key] === this.product?.category);
    return name ? 'categories.' + name : '';
  }

  get isExpired(): boolean {
    const product = this.product;
    if (!product) {
      return false;
    }
    return product.selected === false || product.available === false
      || (!!product.expirationDate && new Date(product.expirationDate).getTime() < Date.now());
  }

  // Jours restants avant expiration (0 = aujourd'hui), null sans date ou déjà expiré
  get daysLeft(): number | null {
    if (!this.product?.expirationDate || this.isExpired) {
      return null;
    }
    return Math.floor((new Date(this.product.expirationDate).getTime() - Date.now()) / 86400000);
  }

  get hasOldPrice(): boolean {
    return !!this.product?.price && this.product.price != this.product.priceDiscount;
  }

  get hasDeliveryPrice(): boolean {
    const price = this.product?.deliveryPrice;
    return price !== undefined && price !== null && price !== '';
  }

  get isFreeDelivery(): boolean {
    return this.hasDeliveryPrice && Number(this.product!.deliveryPrice) === 0;
  }

  get voteLevel(): VoteLevel {
    return getVoteLevel(this.voteScore);
  }

  discountColor(product: Product): string {
    return discountColor(product);
  }

  getVoteLevel(product: Product): VoteLevel {
    return getVoteLevel(product.voteScore);
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString(this.i18n.current, { day: 'numeric', month: 'long', year: 'numeric' });
  }

  timeAgo(date: Date | string): string {
    const seconds = Math.round((new Date(date).getTime() - Date.now()) / 1000);
    const units: [Intl.RelativeTimeFormatUnit, number][] = [['day', 86400], ['hour', 3600], ['minute', 60]];
    const format = new Intl.RelativeTimeFormat(this.i18n.current, { numeric: 'auto' });
    for (const [unit, size] of units) {
      if (Math.abs(seconds) >= size) {
        return format.format(Math.round(seconds / size), unit);
      }
    }
    return format.format(0, 'minute');
  }

  private openLogin() {
    this.dialog.open(DialogContentLogin, {
      width: '550px',
      data: { acceptNewsLetter: false, email: '' },
    });
  }

  onVote(value: 1 | -1) {
    if (!this.product) {
      return;
    }
    if (!this.authService.currentUserValue) {
      this.openLogin();
      return;
    }

    const previousScore = this.voteScore;
    const previousVote = this.myVote;

    // Un même vote ne compte qu'une fois : recliquer ne l'annule pas
    if (previousVote === value) {
      return;
    }
    const delta = previousVote ? value - previousVote : value;

    this.voteScore = previousScore + delta;
    this.myVote = value;

    this.voteService.castVote(this.product._id, value).subscribe({
      next: (result) => {
        this.voteScore = result.voteScore;
        this.myVote = result.myVote;
      },
      error: () => {
        this.voteScore = previousScore;
        this.myVote = previousVote;
      },
    });
  }

  onToggleFavorite() {
    if (!this.product) {
      return;
    }
    if (!this.authService.currentUserValue) {
      this.openLogin();
      return;
    }

    const previous = this.saved;
    this.saved = !previous;

    this.favoriteService.toggleFavorite(this.product._id).subscribe({
      next: (result) => this.saved = result.saved,
      error: () => this.saved = previous,
    });
  }

  // Compteur de vues côté API (seul effet de PUT /products/fromFrontOffice/:id)
  incrementViewNumberProduct() {
    if (!this.product) {
      return;
    }
    this.product = { ...this.product, viewNumber: (this.product.viewNumber || 0) + 1 };
    this.productService.updateProduct(this.product).subscribe({ error: () => { } });
  }

  // Comme la grille : un seul geste copie le code ET ouvre l'offre affiliée (sinon la commission est perdue)
  copyCodeAndOpenOffer() {
    if (!this.product) {
      return;
    }
    this.copyToClipboard(this.product.promotionCode);
    this.codeCopied = true;
    this.snackBar.open(this.i18n.t('deals.codeCopied'), undefined, { duration: 2000 });
    this.incrementViewNumberProduct();
    window.open(this.product.affiliateUrl, '_blank', 'noopener');
  }

  private copyToClipboard(val: string) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(val).catch(() => { });
      return;
    }
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  // Partage de la page PromoDeep du deal (pas du lien affilié) ; sans Web Share, le lien est copié
  sharePage() {
    if (!this.product) {
      return;
    }
    const url = window.location.origin + '/' + pathRoutingEnum.product + '/' + this.product._id;
    if (navigator.share) {
      navigator.share({ title: this.product.name, text: this.product.name, url }).catch(() => { });
    } else {
      this.copyToClipboard(url);
      this.snackBar.open(this.i18n.t('dealDetail.linkCopied'), undefined, { duration: 2000 });
    }
  }

  backClicked() {
    this._location.back();
  }

  scrollToComments() {
    document.getElementById('deal-comments')?.scrollIntoView({ behavior: 'smooth' });
  }

  loadComments() {
    this.commentService.getComments(this.productId).subscribe(comments => {
      this.comments = comments || [];
    });
  }

  postComment() {
    if (!this.newCommentText.trim()) {
      return;
    }
    if (!this.authService.currentUserValue) {
      this.openLogin();
      return;
    }

    this.postingComment = true;
    this.commentService.postComment(this.productId, this.newCommentText.trim()).subscribe({
      next: (comment) => {
        this.comments = [comment, ...this.comments];
        this.updateCommentCount(1);
        this.newCommentText = '';
        this.postingComment = false;
      },
      error: (err) => {
        this.postingComment = false;
        console.error('postComment failed', err);
        const message = this.i18n.t(err?.status === 401 ? 'common.sessionExpired' : 'deals.publishFailed');
        this.snackBar.open(message, this.i18n.t('common.close'), { duration: 4000 });
      },
    });
  }

  isMyComment(comment: Comment): boolean {
    const currentUser = this.authService.currentUserValue;
    return !!currentUser && String(comment.userId) === String(currentUser._id);
  }

  canFollowOrIgnore(comment: Comment): boolean {
    return !!this.authService.currentUserValue && !this.isMyComment(comment) && !!this.accountService.accountValue;
  }

  isFollowedAuthor(comment: Comment): boolean {
    return !!this.accountService.accountValue?.following.some(m => String(m._id) === String(comment.userId));
  }

  isIgnoredAuthor(comment: Comment): boolean {
    return !!this.accountService.accountValue?.ignored.some(m => String(m._id) === String(comment.userId));
  }

  followAuthor(comment: Comment) {
    const request = this.isFollowedAuthor(comment)
      ? this.accountService.unfollow([comment.userId])
      : this.accountService.follow(comment.userId);
    request.subscribe({
      error: (err) => this.snackBar.open(err || this.i18n.t('common.operationUnavailable'), this.i18n.t('common.close'), { duration: 4000 }),
    });
  }

  ignoreAuthor(comment: Comment) {
    const request = this.isIgnoredAuthor(comment)
      ? this.accountService.unignore([comment.userId])
      : this.accountService.ignore(comment.userId);
    request.subscribe({
      // Ignorer un membre met aussi fin à l'amitié côté API
      next: () => this.chatService.loadRelations().subscribe({ error: () => { } }),
      error: (err) => this.snackBar.open(err || this.i18n.t('common.operationUnavailable'), this.i18n.t('common.close'), { duration: 4000 }),
    });
  }

  // Amitié / discussion avec l'auteur du commentaire
  friendRelation(comment: Comment): ChatRelation {
    return this.chatService.relationOf(comment.userId);
  }

  friendActionIcon(comment: Comment): string {
    const icons: Record<ChatRelation, string> = { friend: 'chat', incoming: 'group_add', outgoing: 'schedule', none: 'group_add' };
    return icons[this.friendRelation(comment)];
  }

  friendActionLabel(comment: Comment): string {
    const labels: Record<ChatRelation, string> = {
      friend: 'chat.sendMessageTo',
      incoming: 'chat.acceptRequestFrom',
      outgoing: 'chat.requestPendingWith',
      none: 'chat.addFriendNamed',
    };
    return labels[this.friendRelation(comment)];
  }

  onFriendAction(comment: Comment) {
    const showError = (err: string) =>
      this.snackBar.open(err || this.i18n.t('common.operationUnavailable'), this.i18n.t('common.close'), { duration: 4000 });
    switch (this.friendRelation(comment)) {
      case 'friend':
        this.router.navigate(['/messages', comment.userId]);
        break;
      case 'incoming':
        this.chatService.acceptRequest(this.chatService.incomingRequestFrom(comment.userId)!).subscribe({
          next: () => this.snackBar.open(this.i18n.t('chat.nowFriends', { pseudo: comment.pseudo }), this.i18n.t('common.close'), { duration: 4000 }),
          error: showError,
        });
        break;
      case 'outgoing':
        this.snackBar.open(this.i18n.t('chat.requestPendingWith', { pseudo: comment.pseudo }), this.i18n.t('common.close'), { duration: 4000 });
        break;
      default:
        this.chatService.sendRequest(comment.userId).subscribe({
          next: () => this.snackBar.open(this.i18n.t('chat.requestSentTo', { pseudo: comment.pseudo }), this.i18n.t('common.close'), { duration: 4000 }),
          error: showError,
        });
    }
  }

  startEditComment(comment: Comment) {
    this.editingCommentId = comment._id;
    this.editCommentText = comment.text;
  }

  cancelEditComment() {
    this.editingCommentId = null;
    this.editCommentText = '';
  }

  saveEditComment(comment: Comment) {
    const text = this.editCommentText.trim();
    if (!text) {
      return;
    }
    if (text === comment.text) {
      this.cancelEditComment();
      return;
    }

    this.savingComment = true;
    this.commentService.updateComment(comment._id, text).subscribe({
      next: (updated) => {
        this.comments = this.comments.map(c => c._id === updated._id ? updated : c);
        this.savingComment = false;
        this.cancelEditComment();
      },
      error: (err) => {
        this.savingComment = false;
        this.snackBar.open(this.commentErrorMessage(err, 'edit'), this.i18n.t('common.close'), { duration: 4000 });
      },
    });
  }

  deleteComment(comment: Comment) {
    if (!confirm(this.i18n.t('deals.confirmDelete'))) {
      return;
    }

    this.commentService.deleteComment(comment._id).subscribe({
      next: () => {
        this.comments = this.comments.filter(c => c._id !== comment._id);
        this.updateCommentCount(-1);
        if (this.editingCommentId === comment._id) {
          this.cancelEditComment();
        }
      },
      error: (err) => {
        this.snackBar.open(this.commentErrorMessage(err, 'delete'), this.i18n.t('common.close'), { duration: 4000 });
      },
    });
  }

  // this.product peut venir du store NgRx (objet gelé en dev) : on le remplace au lieu de le modifier
  private updateCommentCount(delta: number) {
    if (this.product) {
      this.product = { ...this.product, commentCount: Math.max(0, (this.product.commentCount || 0) + delta) };
    }
  }

  private commentErrorMessage(err: any, action: 'edit' | 'delete'): string {
    if (err?.status === 401) {
      return this.i18n.t('common.sessionExpired');
    }
    if (err?.status === 403) {
      return this.i18n.t('deals.' + action + 'Forbidden');
    }
    return this.i18n.t('deals.' + action + 'Failed');
  }

}
